import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AddIcon from '@mui/icons-material/Add';
import HomeIcon from '@mui/icons-material/Home';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import { Container, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import React, { useCallback, useEffect, useState } from "react";

import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

import {
  BreadcrumbsComponent,
  LinkItem,
} from '../../components/Breadcrumbs/Breadcrumbs';
import { Spinner } from '../../components/Spinner/Spinner';
import styles from './styles.module.css';
import { useBannersPage } from './useBannersPage';
import { BannerItem } from './components/BannerItem/BannerItem';
import { ChooseModal, MODALS } from './chooseModal';
import { DraggableBannerItem } from './components/DraggableBannerItem';
import { API } from '../../api/api';
import { URLS } from '../../api/urls';
import { useAlert } from '../../context/Alert.context';

const linkList: LinkItem[] = [
  { name: 'Projects', to: '/', icon: <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" /> },
  { name: 'Choice', to: '/choice', icon: <AccountTreeIcon sx={{ mr: 0.5 }} fontSize="inherit" /> },
  { name: 'Banners', to: '', icon: <NewspaperIcon sx={{ mr: 0.5 }} fontSize="inherit" /> },
];

function arrayMove<T>(arr: T[], from: number, to: number) {
  const copy = [...arr];
  const [x] = copy.splice(from, 1);
  copy.splice(to, 0, x);
  return copy;
}

async function reorderBannersRequest(orderedIds: (string | undefined)[], showAlert: any) {
  API.post(`${URLS.BANNERS}/reorder`, { orderedIds })
    .catch((e) => showAlert({ message: 'Cannot reorder', severity: 'error' }))
}

export const BannersPage = () => {
  const { showAlert } = useAlert();
  const {
    loading,
    banners,
    updateBannerAfterDelete,
    updateBannerAfterCreate,
    updateBannerAfterEdit,
    choosenModal,
    selectedBanner,
    closeModal,
    setModal,
    setSelectedPage
  } = useBannersPage();

  const [uiBanners, setUiBanners] = useState(banners);
  const [savingOrder, setSavingOrder] = useState(false);

  useEffect(() => {
    setUiBanners(banners);
  }, [banners]);

  const onDropToIndex = useCallback(async (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;
    if (savingOrder) return;

    const prev = uiBanners;
    const next = arrayMove(uiBanners, fromIndex, toIndex);

    setUiBanners(next);

    setSavingOrder(true);
    try {
      const ods = next.map(b => b.id)
      await reorderBannersRequest(ods, showAlert);

    } catch (e) {
      console.error(e);
      setUiBanners(prev);
    } finally {
      setSavingOrder(false);
    }
  }, [uiBanners, savingOrder]);

  if (loading) {
    return (
      <div className={styles.loader}>
        <Spinner />
      </div>
    );
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <Container maxWidth="xl">
        <div className={styles.container}>
          <div className={styles.header}>
            <Typography variant="h2">Banners</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setModal(MODALS.CREATE_BANNER)}
              disabled={savingOrder}
            >
              Add
            </Button>
          </div>

          <BreadcrumbsComponent linkList={linkList} />

          {!uiBanners.length && (
            <div className={styles.loader}>
              <Typography variant="h2">Empty.</Typography>
            </div>
          )}

          {!!uiBanners.length && (
            <div className={styles.bannersContainer} style={{ opacity: savingOrder ? 0.7 : 1 }}>
              {uiBanners.map((banner, index) => (
                <DraggableBannerItem
                  key={banner.id}
                  index={index}
                  onDropToIndex={onDropToIndex}
                >
                  <BannerItem
                    titleEn={banner.titleEn}
                    subtitleEn={banner.subTitleEn}
                    btnTextEn={banner.btnTextEn}
                    src={banner.image}
                    onEditIcon={() => {
                      setSelectedPage(banner);
                      setModal(MODALS.EDIT_BANNER);
                    }}
                    onDeleteIcon={() => {
                      setSelectedPage(banner);
                      setModal(MODALS.DELETE_BANNER);
                    }}
                    link={banner.link}
                  />
                </DraggableBannerItem>
              ))}
            </div>
          )}
        </div>

        <ChooseModal
          updateBannerAfterDelete={updateBannerAfterDelete}
          updateBannerAfterCreate={updateBannerAfterCreate}
          updateBannerAfterEdit={updateBannerAfterEdit}
          choosenModal={choosenModal}
          selectedBanner={selectedBanner}
          closeModal={closeModal}
        />
      </Container>
    </DndProvider>
  );
};
